import type { KeyboardShortcutHintProps } from "@/components/KeyboardShortcutHint";

export type KeyboardShortcutEntry = {
  description: string;
  hints: KeyboardShortcutHintProps[];
  /** How consecutive hints are joined in the shortcuts dialog. Defaults to `then`. */
  hintConjunction?: "then" | "or";
};

type BuildKeyboardShortcutEntriesInput = {
  showGoNav: boolean;
  /** Omit when Global Search shortcut is disabled (university staff). */
  showGlobalSearch?: boolean;
  addMenuItems: { label: string; chordKey: string }[];
  canViewMcpPlayground: boolean;
  canViewTasks?: boolean;
};

export function buildKeyboardShortcutEntries({
  showGoNav,
  showGlobalSearch = true,
  addMenuItems,
  canViewMcpPlayground,
  canViewTasks = false
}: BuildKeyboardShortcutEntriesInput): KeyboardShortcutEntry[] {
  const entries: KeyboardShortcutEntry[] = [
    ...(showGlobalSearch
      ? [
          {
            description: "Global Search",
            hintConjunction: "or",
            hints: [
              { keyLabel: "k", showModifier: true },
              { keyLabel: "P", showModifier: true, implicitShift: true }
            ]
          } satisfies KeyboardShortcutEntry
        ]
      : []),
    ...(canViewTasks
      ? [
          {
            description: "Tasks",
            hints: [{ keyLabel: "t", showModifier: false }]
          } satisfies KeyboardShortcutEntry
        ]
      : []),
    ...addMenuItems.map((item) => ({
      description: item.label,
      hints: [
        { keyLabel: "a", showModifier: false },
        { keyLabel: item.chordKey, showModifier: false }
      ]
    }))
  ];

  if (showGoNav) {
    entries.push({
      description: "Go to a main section",
      hints: [
        { keyLabel: "g", showModifier: false },
        { keyLabel: "Tab", showModifier: false }
      ]
    });
  }

  entries.push({
    description: "Focus page search or sole text field",
    hints: [{ keyLabel: "/", showModifier: false }]
  });

  entries.push(
    {
      description: "Next table record",
      hints: [{ keyLabel: "j", showModifier: false }]
    },
    {
      description: "Previous table record",
      hints: [{ keyLabel: "k", showModifier: false }]
    },
    {
      description: "Open table record",
      hintConjunction: "or",
      hints: [
        { keyLabel: "o", showModifier: false },
        { keyLabel: "Enter", showModifier: false }
      ]
    },
    {
      description: "Preview table record",
      hints: [{ keyLabel: "Space", showModifier: false }]
    }
  );

  if (canViewMcpPlayground) {
    entries.push({
      description: "Chat with Member 360",
      hints: [{ keyLabel: "?", showModifier: false }]
    });
  }

  entries.push({
    description: "Keyboard Shortcuts",
    hints: [{ keyLabel: "?", showModifier: true, implicitShift: true }]
  });

  return entries;
}
